import { useState } from 'react';
import { describeConflict, resolveConflict, type ConflictView as ConflictData } from '../api';
import { type IdentityLocale, type IdentityTranslate } from '../i18n';
import { Button, Card, Field, Id, Notice, TextInput, Moment } from '../../../shell/kit';
import { CustomerPicker, VehiclePicker } from '../../../shell/pickers';
import { ComparePanel } from '../merge/compare-panel';
import { errorText } from '../../../shell/errors';

export function ConflictView({
  t,
  locale,
}: {
  t: IdentityTranslate;
  locale: IdentityLocale;
}) {
  const [vehicleId, setVehicleId] = useState('');
  const [challengerId, setChallengerId] = useState('');
  const [reason, setReason] = useState('');
  const [data, setData] = useState<ConflictData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (err: unknown): void => {
    setError(errorText(err).message);
  };

  const load = async (): Promise<void> => {
    setError(null);
    setInfo(null);
    try {
      setData(await describeConflict(vehicleId.trim(), challengerId.trim() || undefined));
    } catch (err) {
      fail(err);
    }
  };

  const keep = async (customerId: string): Promise<void> => {
    if (reason.trim().length < 3) {
      setError(t('identity.common.required'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resolveConflict(vehicleId.trim(), customerId, reason);
      setInfo(t('identity.conflict.done'));
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('identity.conflict.hint')}</Notice>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {info ? <Notice tone="ok">{info}</Notice> : null}

      <Card title={t('identity.conflict.title')}>
        {/*
          * Оба поля были пустыми строками под ULID. Экран разбирает спор о том,
          * чья машина, — и начинался он с двух идентификаторов, которых нет ни
          * на машине, ни в карточке клиента. Соседняя вкладка «Слияние» ровно
          * этот же вопрос спрашивает поиском; здесь просто не довели.
          */}
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t('identity.conflict.vehicle')} hint={t('identity.conflict.vehicleHint')}>
            <VehiclePicker
              value={vehicleId}
              onChange={(id) => {
                setVehicleId(id);
                setData(null);
              }}
            />
          </Field>
          <Field
            label={t('identity.conflict.challenger')}
            hint={t('identity.conflict.challengerHint')}
          >
            <CustomerPicker
              value={challengerId}
              onChange={(id) => {
                setChallengerId(id);
                setData(null);
              }}
            />
          </Field>
        </div>
        <div className="mt-3">
          <Field label={t('identity.duplicates.reason')}>
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3">
          <Button onClick={() => void load()} disabled={!vehicleId}>
            {t('identity.conflict.load')}
          </Button>
        </div>
      </Card>

      {data ? (
        <Card>
          <dl className="mb-4 space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="w-40 text-ink-muted">{t('identity.conflict.current')}</dt>
              <dd className="text-ink">
                {data.currentCustomerId ? (
                  <>
                    {data.currentCustomerName ?? t('identity.conflict.noName')}{' '}
                    <Id value={data.currentCustomerId} />
                  </>
                ) : (
                  t('identity.conflict.none')
                )}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-40 text-ink-muted">{t('identity.conflict.since')}</dt>
              <dd>
                <Moment iso={data.since} />
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-40 text-ink-muted">{t('identity.conflict.plate')}</dt>
              <dd className="font-mono">{data.plate}</dd>
            </div>
          </dl>

          {data.comparison ? (
            <ComparePanel
              payload={data.comparison}
              t={t}
              onKeep={(primaryId) => void keep(primaryId)}
            />
          ) : (
            <Notice tone="info">{t('identity.common.nothing')}</Notice>
          )}

          {challengerId && !busy ? (
            <div className="mt-3 flex gap-2">
              <Button tone="primary" onClick={() => void keep(challengerId.trim())}>
                {t('identity.conflict.keep')}
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
